/**
 * BalanceEditModal Component
 * Modal for editing an existing balance anchor via supersession
 * WP-FLOW-12: Balance anchor editing feature
 */
import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Alert from '@/components/ui/Alert';
import { accountsService } from '@/services/accounts';
import { accountKeys } from '@/hooks/useAccounts';
import { ApiClientError } from '@/services/api';
import { toast } from 'sonner';
import type { BalanceEntry, BalanceEntryUpdateRequest } from '@/types';

export interface BalanceEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountId: string;
  entry: BalanceEntry | null;
  currency?: string;
}

export default function BalanceEditModal({
  isOpen,
  onClose,
  accountId,
  entry,
  currency = 'USD',
}: BalanceEditModalProps) {
  const queryClient = useQueryClient();
  const [balanceAmount, setBalanceAmount] = useState('');
  const [availableAmount, setAvailableAmount] = useState('');
  const [asOfDate, setAsOfDate] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Initialize form with entry values when modal opens
  useEffect(() => {
    if (entry && isOpen) {
      setBalanceAmount(entry.balance.toString());
      setAvailableAmount(entry.availableBalance?.toString() ?? '');
      setAsOfDate(entry.effectiveDate);
      setNotes(entry.notes ?? '');
      setError(null);
    }
  }, [entry, isOpen]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: BalanceEntryUpdateRequest) =>
      accountsService.updateBalanceEntry(accountId, entry!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['balance-history', accountId] });
      queryClient.invalidateQueries({ queryKey: accountKeys.all });
      toast.success('Balance entry updated');
      handleClose();
    },
    onError: (err) => {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to update balance entry. Please try again.');
      }
    },
  });

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!entry) return;

    // Build update request with only changed fields
    const updateData: BalanceEntryUpdateRequest = {};
    let hasChanges = false;

    const newBalance = parseFloat(balanceAmount);
    if (!isNaN(newBalance) && newBalance !== entry.balance) {
      updateData.balanceAmount = newBalance;
      hasChanges = true;
    }

    const newAvailable = availableAmount.trim() ? parseFloat(availableAmount) : undefined;
    if (newAvailable !== undefined && newAvailable !== entry.availableBalance) {
      updateData.availableAmount = newAvailable;
      hasChanges = true;
    } else if (newAvailable === undefined && entry.availableBalance !== undefined) {
      // Clear available balance - but backend may not support undefined
      // For now, skip this case
    }

    if (asOfDate && asOfDate !== entry.effectiveDate) {
      updateData.asOfDate = asOfDate;
      hasChanges = true;
    }

    const trimmedNotes = notes.trim();
    if (trimmedNotes !== (entry.notes ?? '')) {
      updateData.notes = trimmedNotes || undefined;
      hasChanges = true;
    }

    if (!hasChanges) {
      setError('No changes detected. Modify at least one field to update.');
      return;
    }

    setError(null);
    updateMutation.mutate(updateData);
  };

  if (!isOpen || !entry) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="balance-edit-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        {/* Header icon */}
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 mb-4">
          <EditIcon className="h-6 w-6 text-blue-600" />
        </div>

        <h2
          id="balance-edit-modal-title"
          className="text-lg font-semibold text-gray-900 text-center mb-2"
        >
          Edit Balance Entry
        </h2>

        <p className="text-sm text-gray-600 text-center mb-4">
          Update the balance entry. This creates a new version and preserves the history.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label={`Balance (${currency})`}
            type="number"
            step="0.01"
            value={balanceAmount}
            onChange={(e) => setBalanceAmount(e.target.value)}
            placeholder="0.00"
            required
          />

          <Input
            label={`Available Balance (${currency})`}
            type="number"
            step="0.01"
            value={availableAmount}
            onChange={(e) => setAvailableAmount(e.target.value)}
            placeholder="0.00 (optional)"
          />

          <Input
            label="Effective Date"
            type="date"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
            required
          />

          <div>
            <label
              htmlFor="notes"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Notes
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes about this balance entry"
              maxLength={1000}
              rows={3}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          {error && (
            <Alert variant="error" onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={updateMutation.isPending}
            >
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
      />
    </svg>
  );
}
